import {
  faImage,
  faLightbulb,
  faPaperPlane,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Image, Modal, Nav, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { submitAppFeedback } from 'services/api/feedback';

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

interface SelectedImage {
  file: File;
  previewUrl: string;
}

interface FeedbackNavItemProps {
  slim?: boolean;
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    sizes.length - 1
  );

  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${
    sizes[index]
  }`;
};

const getErrorMessage = (error: any) => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.messages ||
    error?.response?.data?.data?.message ||
    error?.message;

  if (Array.isArray(message)) {
    return message[0] || 'Failed to submit feedback.';
  }

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return 'Failed to submit feedback.';
};

const FeedbackNavItem = ({ slim = false }: FeedbackNavItemProps) => {
  const [show, setShow] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageError, setMessageError] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { pathname } = useLocation();
  const { authUser } = useAccessToken();

  useEffect(() => {
    return () => {
      if (selectedImage?.previewUrl) {
        URL.revokeObjectURL(selectedImage.previewUrl);
      }
    };
  }, [selectedImage]);

  const clearSelectedImage = () => {
    if (selectedImage?.previewUrl) {
      URL.revokeObjectURL(selectedImage.previewUrl);
    }

    setSelectedImage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setMessage('');
    setMessageError('');
    clearSelectedImage();
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    resetForm();
    setShow(false);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error('Image size must be 10MB or less.');
      event.target.value = '';
      return;
    }

    if (selectedImage?.previewUrl) {
      URL.revokeObjectURL(selectedImage.previewUrl);
    }

    setSelectedImage({
      file,
      previewUrl: URL.createObjectURL(file)
    });
  };

  const handleSubmit = async () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      setMessageError('Please share your idea before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitAppFeedback({
        message: trimmedMessage,
        image: selectedImage?.file || null,
        route: pathname,
        pageUrl: window.location.href,
        source: 'top-header',
        submittedByName: authUser
          ? `${authUser.firstName || ''} ${authUser.lastName || ''}`.trim()
          : undefined,
        submittedByEmail: authUser?.email,
        userAgent: window.navigator.userAgent
      });

      toast.success('Feedback submitted successfully.');
      setShow(false);
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Nav.Item>
        <Nav.Link
          onClick={() => setShow(true)}
          className={classNames({
            'py-0': slim,
            'h-100': !slim
          })}
          title="Share feedback"
          aria-label="Share feedback"
        >
          <FontAwesomeIcon
            icon={faLightbulb}
            style={{ fontSize: slim ? 12 : 18 }}
          />
        </Nav.Link>
      </Nav.Item>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        backdrop={isSubmitting ? 'static' : true}
        keyboard={!isSubmitting}
      >
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title>Help Improve the App</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-muted mb-3">
            Do you have any idea to make the app better?
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Your feedback</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={message}
              onChange={event => {
                setMessage(event.target.value);

                if (messageError && event.target.value.trim()) {
                  setMessageError('');
                }
              }}
              isInvalid={Boolean(messageError)}
              placeholder="Share an idea, issue, or workflow improvement..."
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {messageError}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faImage} className="me-2" />
                Add image
              </Button>
              <span className="fs-10 text-muted">
                Optional screenshot or mockup
              </span>
            </div>

            {selectedImage && (
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={clearSelectedImage}
                disabled={isSubmitting}
              >
                Remove image
              </Button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            hidden
          />

          {selectedImage && (
            <div className="border rounded-3 p-2 mt-3">
              <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                <div className="flex-grow-1">
                  <div className="fw-semibold text-truncate">
                    {selectedImage.file.name}
                  </div>
                  <div className="fs-10 text-muted">
                    {formatFileSize(selectedImage.file.size)}
                  </div>
                </div>

                <Button
                  variant="link"
                  className="p-0 text-body"
                  onClick={clearSelectedImage}
                  disabled={isSubmitting}
                  aria-label="Remove selected image"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </div>

              <Image
                src={selectedImage.previewUrl}
                alt={selectedImage.file.name}
                rounded
                fluid
                style={{
                  maxHeight: 220,
                  width: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Sending...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Send feedback
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FeedbackNavItem;
